import { Component, inject, signal, computed, OnInit, OnDestroy, AfterViewInit, TemplateRef, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router, ActivatedRoute } from "@angular/router";
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatTableModule } from "@angular/material/table";
import { MatCardModule } from "@angular/material/card";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatSnackBar, MatSnackBarModule, MatSnackBarRef } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { Subject, takeUntil, Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import {
  LucideAngularModule,
  BookOpen,
  Edit,
  Trash2,
  Upload,
  FileText,
  X,
  Plus,
  Check,
  Pen,
  FileWarning,
  Info,
  Search,
  SlidersHorizontal,
  Eye,
  BadgeCheck,
  PackageCheck,
  OctagonAlert,
  PackageX,
  Star,
  StarOff,
  ChevronDown,
  EllipsisVertical,
  Headset,
  ScanText,
  ClipboardPen,
  TriangleAlert,
  RefreshCw,
  History,
  ArrowLeft,
  ChevronRight,
  FlaskConical,
  Download,
  ListChecks,
  SearchCheck
} from "lucide-angular";
import { ArticleListItem, BatchCreateDraftsResponse, KnowledgeBase, ArticleHistoryResponse, ImportJobDownloadResponse } from "../../core/models/knowledge-base.model";
import { KnowledgeBaseService } from '../../core/services/knowledge-base.service';
import { PicklistService } from './services/picklist.service';
import { DataTableComponent, TableColumn, TableAction, TableConfig } from '../../shared/components/data-table';
import { ArticleEditModalComponent } from './components/article-edit/article-edit-modal.component';
import { PicklistEditModalComponent } from './components/picklist-edit/picklist-edit-modal.component';
import { FilterModalComponent } from './components/filters/filter-modal.component';
import { ArticlePublishConfirmModalComponent, ArticlePublishConfirmModalData } from './components/article-publish-confirm/article-publish-confirm-modal.component';
import { ArticleExportModalComponent, ArticleExportModalData } from "./components/article-export/article-export-modal.component";
import { ArticleDeleteModalComponent, ArticleDeleteModalResult } from "./components/article-delete-modal.component/article-delete-modal.component";
import { CellContentModalComponent, CellContentModalData } from './components/cell-content-modal/cell-content-modal.component';
import { RetrievalTestModalComponent } from './components/retrieval-test/retrieval-test-modal.component';
import { A11yModule } from "@angular/cdk/a11y";
import * as XLSX from 'xlsx';
import { RecentItemsService } from "../../core/services/recent-items.service";

@Component({
  selector: "app-knowledge-base-page",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    LucideAngularModule,
    DataTableComponent,
    MatSnackBarModule,
    MatTooltipModule,
    MatSelectModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    A11yModule,
],
  templateUrl: "./knowledge-base-page.component.html",
  styleUrl: "./knowledge-base-page.component.scss"
})
export class KnowledgeBasesPageComponent implements OnInit, OnDestroy, AfterViewInit {
  // Track selected rows in the table
  selectedRows: ArticleListItem[] = [];
  readonly snackBar = inject(MatSnackBar);
  readonly breakpointObserver = inject(BreakpointObserver);
  readonly recentItemsService = inject(RecentItemsService);

  readonly destroy$ = new Subject<void>();
  
  // Mobile detection
  isMobile = signal<boolean>(false);

  // Cache para páginas ya cargadas
  private readonly pageCache = new Map<string, { articles: ArticleListItem[], pagination: { totalItems: number, totalPages: number } }>();

  loading = signal<boolean>(false);
  isLoadingKnowledgeBase = signal<boolean>(false);
  filtersOpen = signal<boolean>(false);
  loadingChip = signal<string | null>(null);
  notDraft = signal<boolean>(true);
  environmetFilter = signal<string>('Producción'); // Default to PROD
  environmetOptions = ['Producción', 'QA', 'Borrador', 'Eliminados'];
  tableFilterOptions = ['Tipo', 'Categoría 1', 'Categoría 2 Salesforce', 'Categoría 2', 'Categoría 1 Salesforce', 'Accioón', 'Id de solicitud'];
  tableFilterContent = signal<string>('');
  articles = signal<ArticleListItem[]>([]);
  knowledgeBase = signal<KnowledgeBase | null>(null);
  
  // Current article dialog reference for managing navigation between versions
  private currentArticleDialogRef: MatDialogRef<ArticleEditModalComponent> | null = null;
  
  // Track currently loading/opened article to prevent duplicate loads
  private currentArticleKey: string | null = null;
  private isLoadingArticle = false;
  
  // Picklists data - stored once for reuse in modals
  picklistsData = signal<{ types: any[], items: { [typeId: number]: any[] } } | null>(null);
  
  // Active filters from the filter modal
  activeFilters = signal<{ [key: string]: number[] } | null>(null);
  
  // Pagination state
  currentPage = signal<number>(1);
  pageSize = signal<number>(20);
  totalItems = signal<number>(0);
  totalPages = signal<number>(0);

  // Icons
  readonly BookOpen = BookOpen;
  readonly Edit = Edit;
  readonly Trash2 = Trash2;
  readonly Upload = Upload;
  readonly FileText = FileText;
  readonly X = X;
  readonly Plus = Plus;
  readonly Check = Check;
  readonly Pen = Pen;
  readonly FileWarning = FileWarning;
  readonly Info = Info;
  readonly Search = Search;
  readonly SlidersHorizontal = SlidersHorizontal;
  readonly Eye = Eye;
  readonly PackageCheck = PackageCheck;
  readonly OctagonAlert = OctagonAlert;
  readonly BadgeCheck = BadgeCheck;
  readonly PackageX = PackageX;
  readonly Star = Star;
  readonly StarOff = StarOff;
  readonly ChevronDown = ChevronDown;
  readonly EllipsisVertical = EllipsisVertical;
  readonly Headset = Headset;
  readonly ScanText = ScanText;
  readonly ClipboardPen = ClipboardPen;
  readonly TriangleAlert = TriangleAlert;
  readonly History = History;
  readonly ArrowLeft = ArrowLeft;
  readonly ChevronRight = ChevronRight;
  readonly FlaskConical = FlaskConical;
  readonly Download = Download;
  readonly ListChecks = ListChecks;
  readonly SearchCheck = SearchCheck;

  // custom column table templates
  @ViewChild('identifierTemplate') identifierTemplate?: TemplateRef<unknown>;
  @ViewChild('statusTemplate') statusTemplate?: TemplateRef<unknown>;
  @ViewChild('actionsTemplate') actionsTemplate?: TemplateRef<unknown>;
  @ViewChild('questionTemplate') questionTemplate?: TemplateRef<unknown>;
  @ViewChild('contentTemplate') contentTemplate?: TemplateRef<unknown>;
  @ViewChild('keywordsTemplate') keywordsTemplate?: TemplateRef<unknown>;

  constructor(
    readonly knowledgeBaseService: KnowledgeBaseService,
    readonly picklistService: PicklistService,
    readonly dialog: MatDialog,
    readonly router: Router,
    readonly route: ActivatedRoute
  ) {
    // Detectar cambios en el tamaño de pantalla (mobile < 768px)
    this.breakpointObserver
      .observe(['(max-width: 767px)'])
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isMobile.set(result.matches);
      });
  }

  /**
   * Configuración estática de iconos por estado
   */
  private readonly STATUS_ICON_CONFIG: Record<string, { icon: any; tooltip: string; cssClass: string; showSpinner: boolean }> = {
    // actions
    'TRANSFER_TO_CASE_AGENT': {
      icon: ClipboardPen,
      tooltip: 'TRANSFER_TO_CASE_AGENT',
      cssClass: 'text-green-600 dark:text-green-400',
      showSpinner: false
    },
    'FIXED_ANSWER': {
      icon: ScanText,
      tooltip: 'FIXED_ANSWER',
      cssClass: 'text-amber-400 dark:text-amber-400',
      showSpinner: false
    },
    'TRANSFER_TO_HUMAN_IMMEDIATELY': {
      icon: Headset,
      tooltip: 'TRANSFER_TO_HUMAN_IMMEDIATELY',
      cssClass: 'text-red-600 dark:text-red-400',
      showSpinner: false
    },
    // states
    'CANDIDATE': {
      icon: PackageCheck,
      tooltip: 'Candidato',
      cssClass: 'w-5 h-5 text-blue-500 flex-shrink-0',
      showSpinner: false
    },
    'SYNCHRONIZING': {
      icon: null,
      tooltip: 'Este artículo se está sincronizando, una vez finalizado el proceso, estará diponible para los bots',
      cssClass: '',
      showSpinner: true
    },
    'PUBLISHED:QA': {
      icon: Star,
      tooltip: 'Esta versión es la que utilizan los bots en QA, pero aún no está en Producción',
      cssClass: 'w-5 h-5 text-orange-500 flex-shrink-0',
      showSpinner: false
    },
    'QA_PUBLISHED': {
      icon: Star,
      tooltip: 'Esta versión es la que utilizan los bots en QA, pero aún no está en Producción',
      cssClass: 'w-5 h-5 text-orange-500 flex-shrink-0',
      showSpinner: false
    },
    'PUBLISHED:PROD': {
      icon: Star,
      tooltip: 'Esta es la versión que utilizan los bots en Producción',
      cssClass: 'w-5 h-5 text-green-500 flex-shrink-0',
      showSpinner: false
    },
    'PROD_PUBLISHED': {
      icon: Star,
      tooltip: 'Esta es la versión que utilizan los bots en Producción',
      cssClass: 'w-5 h-5 text-green-500 flex-shrink-0',
      showSpinner: false
    },
    'PROMOTED': {
      icon: BadgeCheck,
      tooltip: 'Esta es la versión que utilizan los bots en QA y también en Producción',
      cssClass: 'w-5 h-5 text-green-500 flex-shrink-0',
      showSpinner: false
    },
    'FAILED': {
      icon: OctagonAlert,
      tooltip: 'Falló la sincronización de este artículo, no está disponible para los bots. Por favor revise el artículo e intente publicar nuevamente',
      cssClass: 'w-5 h-5 text-red-500 flex-shrink-0',
      showSpinner: false
    },
    'SUPERSEDED:QA': {
      icon: StarOff,
      tooltip: 'Sustituido',
      cssClass: 'w-5 h-5 text-orange-500 flex-shrink-0',
      showSpinner: false
    },
    'SUPERSEDED:PROD': {
      icon: StarOff,
      tooltip: 'Sustituido',
      cssClass: 'w-5 h-5 flex-shrink-0',
      showSpinner: false
    },
    'SUPERSEDED': {
      icon: StarOff,
      tooltip: 'Sustituido',
      cssClass: 'w-5 h-5 flex-shrink-0',
      showSpinner: false
    },
    'DEPRECATED': {
      icon: PackageX,
      tooltip: 'Descartado',
      cssClass: 'w-5 h-5 flex-shrink-0',
      showSpinner: false
    }
  };

  /**
   * Retorna la configuración de icono, tooltip y clases CSS basado en el statusCode y state
   */
  getStatusIconConfig(row: ArticleListItem): { icon: any; tooltip: string; cssClass: string; showSpinner: boolean } {
    if (row.isActive === false) {
      return {
        icon: Trash2,
        tooltip: 'Este artículo ha sido eliminado de todos los ambientes',
        cssClass: 'w-5 h-5 text-red-500 flex-shrink-0',
        showSpinner: false
      };
    }

    const { statusCode, state } = row;
    
    if (!statusCode) {
      return { icon: null, tooltip: '', cssClass: '', showSpinner: false };
    }
    
    // Intentar buscar con estado específico primero
    const keyWithState = `${statusCode}:${state}`;
    const config = this.STATUS_ICON_CONFIG[keyWithState] || this.STATUS_ICON_CONFIG[statusCode];
    
    // Retornar configuración encontrada o caso por defecto
    return config || {
      icon: null,
      tooltip: '',
      cssClass: '',
      showSpinner: false
    };
  }

  /**
   * Retorna la configuración de icono para acciones basado en el actionsPicklistItemId
   */
  getActionIconConfig(row: ArticleListItem): { icon: any; tooltip: string; cssClass: string; showSpinner: boolean } {
    if (!row.actionsPicklistItemId) {
      return { icon: null, tooltip: '-', cssClass: '', showSpinner: false };
    }

    // Obtener el código de la acción desde picklistsData
    const actionCode = this.getActionCode(row.actionsPicklistItemId);
    
    if (!actionCode) {
      // Si no hay código, mostrar el nombre desde picklistService
      const actionName = this.picklistService.getItemName(row.actionsPicklistItemId);
      return { 
        icon: null, 
        tooltip: actionName, 
        cssClass: '', 
        showSpinner: false 
      };
    }

    // Convertir el código a mayúsculas para buscar en la configuración
    const configKey = actionCode.toUpperCase();
    const config = this.STATUS_ICON_CONFIG[configKey];
    
    return config || {
      icon: null,
      tooltip: actionCode,
      cssClass: '',
      showSpinner: false
    };
  }

  /**
   * Obtiene el código de una acción desde picklistsData
   */
  private getActionCode(actionsPicklistItemId: number): string | null {
    const picklists = this.picklistsData();
    if (!picklists) return null;

    // Buscar en todos los items de todos los tipos
    for (const typeId in picklists.items) {
      const items = picklists.items[typeId];
      const action = items.find(item => item.picklistItemId === actionsPicklistItemId);
      if (action) {
        return action.code;
      }
    }

    return null;
  }

  /**
   * Convierte el string de keywords en un array de palabras clave
   */
  getKeywordsArray(keywords: string | undefined): string[] {
    if (!keywords) return [];
    return keywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);
  }

  openRetrievalTest(): void {
    const kb = this.knowledgeBase();
    if (!kb) return;
    this.dialog.open(RetrievalTestModalComponent, {
      data: { kbId: kb.knowledgeBaseId, kbName: kb.name },
      width: '900px',
      maxWidth: '95vw',
      height: '90vh',
      maxHeight: '95vh',
      panelClass: 'custom-dialog-container',
    });
  }

  openSimilarityCheck(article: ArticleListItem): void {
    const kb = this.knowledgeBase();
    if (!kb || !article.question) return;
    this.dialog.open(RetrievalTestModalComponent, {
      data: {
        kbId: kb.knowledgeBaseId,
        kbName: kb.name,
        initialQuestion: article.question,
        excludeIdentifier: article.identifier,
      },
      width: '900px',
      maxWidth: '95vw',
      height: '90vh',
      maxHeight: '95vh',
      panelClass: 'custom-dialog-container',
    });
  }

  /**
   * Abre un modal para mostrar el contenido completo de una celda (pregunta o contenido)
   */
  openCellContentModal(title: string, content: string): void {
    this.dialog.open(CellContentModalComponent, {
      data: {
        title,
        content,
        isKeywords: false
      } as CellContentModalData,
      maxWidth: '90vw',
      maxHeight: '80vh',
      minWidth: '300px',
      autoFocus: false
    });
  }

  /**
   * Abre un modal para mostrar todas las palabras clave
   */
  openKeywordsModal(keywords: string | undefined): void {
    const keywordsArray = this.getKeywordsArray(keywords);
    this.dialog.open(CellContentModalComponent, {
      data: {
        title: 'Todas las palabras clave',
        content: '',
        isKeywords: true,
        keywords: keywordsArray
      } as CellContentModalData,
      maxWidth: '90vw',
      maxHeight: '80vh',
      minWidth: '300px',
      autoFocus: false
    });
  }

  // Table columns
  tableColumns: TableColumn<ArticleListItem>[] = [
    {
      key: 'identifier',
      header: 'Identificador',
      type: 'custom',
      searchable: true
    },
    { 
      key: 'category1PicklistItemId', 
      header: 'Categoría 1', 
      type: 'text', 
      searchable: true, 
      formatter: (v: number) => this.picklistService.getItemName(v)
    },
    { 
      key: 'category2SalesforcePicklistItemId', 
      header: 'Categoría 2 Salesforce', 
      type: 'text', 
      searchable: true, 
      formatter: (v: number) => this.picklistService.getItemName(v)
    },
    { key: 'actionsPicklistItemId', header: 'Acción', type: 'text', searchable: true, visible: true, formatter: (v: string) => v || '-' },
    { key: 'question', header: 'Pregunta', type: 'custom', searchable: true },
    { key: 'content', header: 'Contenido', type: 'custom', searchable: true },
    { 
      key: 'keywords', 
      header: 'Palabras clave', 
      type: 'custom', 
      searchable: true
    },
    { 
      key: 'publications', 
      header: 'Publicaciones', 
      type: 'chips', 
      visible: false, // Solo visible en ambiente Borrador
      chipsConfig: {
        getItems: (row: ArticleListItem) => {
          const items = [];
          if (row.currentQaVersionNumber !== null && row.currentQaVersionNumber !== undefined) {
            items.push({ 
              label: `QA: v${row.currentQaVersionNumber}`, 
              color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
              environment: 'QA',
              version: row.currentQaVersionNumber,
              hasVersion: true
            });
          } else {
            items.push({ 
              label: 'QA: X', 
              color: 'bg-gray-200 text-gray-600 dark:bg-gray-800/40 dark:text-gray-400',
              environment: 'QA',
              hasVersion: false
            });
          }
          
          if (row.currentProdVersionNumber !== null && row.currentProdVersionNumber !== undefined) {
            items.push({ 
              label: `PROD: v${row.currentProdVersionNumber}`, 
              color: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
              environment: 'PROD',
              version: row.currentProdVersionNumber,
              hasVersion: true
            });
          } else {
            items.push({ 
              label: 'PROD: X', 
              color: 'bg-gray-200 text-gray-600 dark:bg-gray-800/40 dark:text-gray-400',
              environment: 'PROD',
              hasVersion: false
            });
          }
          
          return items;
        },
        labelProperty: 'label',
        colorProperty: 'color',
        emptyMessage: 'Sin publicaciones',
        onClick: (chipItem: any, row: ArticleListItem) => this.onPublicationChipClick(chipItem, row),
        isClickable: (chipItem: any) => chipItem.hasVersion === true,
        isLoading: (chipItem: any, row: ArticleListItem) => this.loadingChip() === `${row.knowledgeArticleId}-${chipItem.environment}`
      }
    },
    { key: 'statusCode', header: 'Estado', type: 'text', searchable: true, visible: true, formatter: (v: string) => v || '-' },
    { key:'lastModifiedAt', header: 'Última modificación', type: 'text',
        formatter: (value: any, article: ArticleListItem) => {
          const date = new Date(article.lastModifiedAt).toLocaleDateString('es-CL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
          let by = '';
          if (article.lastModifiedByEmail) {
            by = ` (Por: ${article.lastModifiedByEmail})`;
          }
          return date + by;
        },
        sortable: true,
    }
  ];

  // Definición de acciones
  tableConfig: TableConfig = {
    searchable: false,
    pageable: true,
    pageSizeOptions: [10, 20, 50, 100],
    defaultPageSize: 20,
    stickyHeader: true, 
    emptyMessage: 'No se encontraron artículos',
    emptyLucideIcon: FileWarning,
    loadingMessage: 'Cargando artículos...',
    resultsSingular: 'artículo',
    resultsPlural: 'artículos',
    maxHeight: 'calc(100vh - 240px)',
    infiniteScroll: false, // Disable infinite scroll when using server-side pagination
    scrollThreshold: 100,
    showResultsCount: false, // Disable results count for server pagination
    multiSelect: true, // Habilitar selección múltiple
    selectAllTooltip: 'Seleccionar todos los artículos',
    selectTooltip: 'Seleccionar artículo',
    showActionsColumnHeader: false,
    useCompactActions: true // Usar menú desplegable para acciones
  };

  // Table actions - computed based on environment using signal
  tableActions = computed<TableAction<ArticleListItem>[]>(() => {
    const env = this.environmetFilter();
    const isDeleted = env === 'Eliminados';
    const isReadOnlyEnvironment = isDeleted || env === 'Producción' || env === 'QA';
    
    const viewAction = { 
      icon: isReadOnlyEnvironment ? 'eye' : 'pen', 
      lucideIcon: isReadOnlyEnvironment ? Eye : Pen, 
      tooltip: isReadOnlyEnvironment ? 'Ver detalles del artículo' : 'Editar borrador del artículo', 
      callback: (a: ArticleListItem) => this.openEditModal(a) 
    };

    if (isDeleted) {
      return [viewAction];
    }

    return [
      viewAction,
      {
        icon: 'search-check', lucideIcon: SearchCheck, tooltip: 'Verificar similitud',
        isVisible: (a: ArticleListItem) => !!a.question,
        callback: (a: ArticleListItem) => this.openSimilarityCheck(a),
      },
      {
        icon: 'upload', lucideIcon: Upload, tooltip: 'Publicar artículo',
        isVisible: (a: ArticleListItem) => a.state !== 'PROD' &&
          a.statusCode !== 'CANDIDATE' &&
          a.statusCode !== 'PROMOTED' &&
          a.statusCode !== 'SYNCHRONIZING' &&
          a.statusCode !== 'QA_SYNCHRONIZING' &&
          a.statusCode !== 'PROD_SYNCHRONIZING',
        callback: (a: ArticleListItem) => a.knowledgeArticleId && this.publishArticle(a)
      }
    ];
  });

  ngOnInit(): void {
    this.loading.set(true);
    this.isLoadingKnowledgeBase.set(true);
    this.initializeKnowledgeBase();
    this.setupQueryParamsListener();
  }

  /**
   * Initialize knowledge base from router state or load from API
   * Complexity: 5 (1 || + 1 if + 1 || at level 1 + 1 if at level 1 + 1 else at level 2)
   */
  private initializeKnowledgeBase(): void {
    const knowledgeBaseFromState = this.getKnowledgeBaseFromState();
    
    if (knowledgeBaseFromState) {
      this.knowledgeBase.set(knowledgeBaseFromState);
      this.recentItemsService.addRecentKnowledgeBase(
        knowledgeBaseFromState.code,
        knowledgeBaseFromState.name,
        ['/knowledge-base', knowledgeBaseFromState.code]
      );
      this.isLoadingKnowledgeBase.set(false);
      this.loadPicklistsAndData();
      return;
    }
    
    this.loadKnowledgeBaseFromRoute();
  }

  /**
   * Get knowledge base from router navigation or history state
   * Complexity: 1 (1 ||)
   */
  private getKnowledgeBaseFromState(): KnowledgeBase | undefined {
    const navigation = this.router.getCurrentNavigation();
    const stateKnowledgeBase = navigation?.extras?.state?.['knowledgeBase'] as KnowledgeBase | undefined;
    const historyState = history.state?.['knowledgeBase'] as KnowledgeBase | undefined;
    return stateKnowledgeBase || historyState;
  }

  /**
   * Load knowledge base from route params
   * Complexity: 3 (1 || + 1 if + 1 else)
   */
  private loadKnowledgeBaseFromRoute(): void {
    const code = this.route.snapshot.params['code'] || this.route.snapshot.params['id'];
    
    if (code) {
      this.loadKnowledgeBaseByCode(code);
    } else {
      console.error('No code found in route params');
      this.router.navigate(['/knowledge-base']);
    }
  }

  /**
   * Setup listener for query params to open article modals
   * Complexity: 4 (1 if + 2 || at level 1 + 1 if at level 1 + 1 && at level 1 + 1 ternary at level 1 + 1 else if)
   */
  private setupQueryParamsListener(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.handleQueryParamsChange(params);
      });
  }

  /**
   * Handle query params changes for article modal navigation
   * Complexity: 6 (1 if + 2 || at level 1 + 1 if at level 1 + 1 && at level 1 + 1 ternary at level 1 + 1 else if)
   */
  private handleQueryParamsChange(params: any): void {
    const articleId = params['article'];
    
    if (!articleId) {
      this.clearCurrentArticleKeyIfNeeded();
      return;
    }
    
    const articleKey = this.buildArticleKey(articleId, params['env'], params['version']);
    
    if (this.shouldOpenArticle(articleKey)) {
      const articleData = history.state?.['articleData'] as ArticleListItem | undefined;
      const version = params['version'] ? +params['version'] : undefined;
      this.openArticleModalFromRoute(+articleId, params['env'], version, articleData);
    }
  }

  /**
   * Build unique article key from params
   * Complexity: 2 (2 ||)
   */
  private buildArticleKey(articleId: string, env?: string, version?: string): string {
    return `${articleId}-${env || 'DRAFT'}-${version || 'current'}`;
  }

  /**
   * Check if article should be opened
   * Complexity: 1 (1 &&)
   */
  private shouldOpenArticle(articleKey: string): boolean {
    return this.currentArticleKey !== articleKey && !this.isLoadingArticle;
  }

  /**
   * Clear current article key if needed
   * Complexity: 1 (1 if)
   */
  private clearCurrentArticleKeyIfNeeded(): void {
    if (this.currentArticleKey) {
      this.currentArticleKey = null;
    }
  }

  ngAfterViewInit(): void {
    if (!this.identifierTemplate || !this.statusTemplate) {
      return;
    }

    // Actualizar las columnas con los templates personalizados
    const identifierCol = this.tableColumns.find(col => col.key === 'identifier');
    if (identifierCol) {
      identifierCol.type = 'custom';
      identifierCol.customTemplate = this.identifierTemplate;
    }

    const statusCol = this.tableColumns.find(col => col.key === 'statusCode');
    if (statusCol) {
      statusCol.type = 'custom';
      statusCol.customTemplate = this.statusTemplate;
    }

    const actionsCol = this.tableColumns.find(col => col.key === 'actionsPicklistItemId');
    if (actionsCol) {
      actionsCol.type = 'custom';
      actionsCol.customTemplate = this.actionsTemplate;
    }

    const questionCol = this.tableColumns.find(col => col.key === 'question');
    if (questionCol && this.questionTemplate) {
      questionCol.customTemplate = this.questionTemplate;
    }

    const contentCol = this.tableColumns.find(col => col.key === 'content');
    if (contentCol && this.contentTemplate) {
      contentCol.customTemplate = this.contentTemplate;
    }

    const keywordsCol = this.tableColumns.find(col => col.key === 'keywords');
    if (keywordsCol && this.keywordsTemplate) {
      keywordsCol.customTemplate = this.keywordsTemplate;
    }
  }

  /**
   * Actualiza la visibilidad de las columnas según el ambiente y crea nueva referencia del array
   * para que Angular detecte el cambio
   */
  private updateColumnVisibility(isDraft: boolean): void {
    this.tableColumns = this.tableColumns.map(col => {
      // Columna de estado: visible solo cuando NO es draft
      if (col.key === 'statusCode') {
        return { ...col, visible: !isDraft };
      }
      // Columna de publicaciones: visible solo cuando ES draft
      if (col.key === 'publications') {
        return { ...col, visible: isDraft };
      }
      return col;
    });
  }

  /**
   * Handle environment filter change
   */
  onEnvironmentChange(): void {
    this.loading.set(true);
    const selectedEnvironment = this.environmetFilter();    
    const isDraft = selectedEnvironment === 'Borrador';
    this.notDraft.set(!isDraft);
    this.updateColumnVisibility(isDraft);
    
    this.clearCache(); // Clear cache when environment changes
    this.currentPage.set(1); // Reset to first page when environment changes
    this.loadData(); // Reload articles with new environment
  }

  /**
   * Handle page change from data table
   */
  onPageChange(event: { pageIndex: number; pageSize: number }): void {
    const newPageSize = event.pageSize;
    const oldPageSize = this.pageSize();
    
    // Clear cache if page size changed
    if (newPageSize !== oldPageSize) {
      this.clearCache();
    }
    
    this.currentPage.set(event.pageIndex + 1); // Convert from 0-indexed to 1-indexed
    this.pageSize.set(newPageSize);
    this.loadData();
  }

  /**
   * Load knowledge base by code when navigation state is not available
   */
  private loadKnowledgeBaseByCode(code: string): void {
    this.loading.set(true);
    
    this.knowledgeBaseService.getKnowledgeBaseByCode(code)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (knowledgeBase) => {
          if (knowledgeBase) {
            this.knowledgeBase.set(knowledgeBase);
            this.recentItemsService.addRecentKnowledgeBase(
              knowledgeBase.code,
              knowledgeBase.name,
              ['/knowledge-base', knowledgeBase.code]
            );
            this.isLoadingKnowledgeBase.set(false); // KB cargada desde API
            this.loadPicklistsAndData();
          } else {
            this.loading.set(false);
            this.isLoadingKnowledgeBase.set(false);
            this.snackBar.open('Base de conocimiento no encontrada', 'Cerrar', { duration: 3000 });
            this.router.navigate(['/knowledge-base']);
          }
        },
        error: (error) => {
          console.error('Error loading knowledge base:', error);
          this.loading.set(false);
          this.isLoadingKnowledgeBase.set(false);
          this.snackBar.open('Error al cargar la base de conocimiento. Error: ' + error.message, 'Cerrar', { duration: 3000 });
          this.router.navigate(['/knowledge-base']);
        }
      });
  }

  /**
   * Refresh knowledge base state (synchronization status) and reload articles
   */
  refreshKnowledgeBase(): void {
    const currentKb = this.knowledgeBase();
    if (!currentKb) return;
    
    this.loading.set(true);
    
    this.knowledgeBaseService.getKnowledgeBaseByCode(currentKb.code)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (knowledgeBase) => {
          if (knowledgeBase) {
            this.knowledgeBase.set(knowledgeBase);
            
            // Reload articles to show updated sync status
            this.clearCache();
            this.loadData();
            
            this.snackBar.open('Estado de sincronización actualizado', 'Cerrar', { duration: 2000 });
          } else {
            this.loading.set(false);
          }
        },
        error: (error) => {
          console.error('[PAGE] Error refreshing knowledge base state:', error);
          this.loading.set(false);
          this.snackBar.open('Error al actualizar el estado de sincronización', 'Cerrar', { duration: 3000 });
        }
      });
  }

  /**
   * Load picklists first, then load article data
   */
  private loadPicklistsAndData(): void {
    const kbId = this.knowledgeBase()?.knowledgeBaseId;
    if (!kbId) return;
    // Load picklists for column formatting and store them for modal reuse
    this.picklistService.loadAllPicklists(kbId).subscribe({
      next: (success: boolean) => {
        if (success) {
          // Load and store picklists data for modal reuse
          this.loadAndStorePicklists();
        } else {
          console.warn('Failed to load some picklists');
        }
        // Load article data regardless of picklist loading success
        this.loadData();
      },
      error: (error) => {
        console.error('Error loading picklists:', error);
        // Still load article data even if picklists fail
        this.loadData();
      }
    });
  }

  /**
   * Load and store picklists data once for reuse in modals
   */
  private loadAndStorePicklists(): void {
    this.getAllPicklists().subscribe({
      next: (picklists) => {
        this.picklistsData.set(picklists);
      },
      error: (error) => {
        console.error('Error loading picklists data:', error);
      }
    });
  }

  loadData(): void {
    const kb = this.knowledgeBase();
    if (!kb) return;

    const selectedEnvironment = this.environmetFilter();
    const isDraft = selectedEnvironment === 'Borrador';
    this.notDraft.set(!isDraft);
    this.updateColumnVisibility(isDraft);
    
    const page = this.currentPage();
    const cacheKey = this.getCacheKey(page);
    
    // Check if page is already cached
    const cachedData = this.pageCache.get(cacheKey);
    if (cachedData) {
      this.articles.set(cachedData.articles);
      this.totalItems.set(cachedData.pagination.totalItems);
      this.totalPages.set(cachedData.pagination.totalPages);
      return; // No need to set loading or make API call
    }
    
    // Not in cache, load from API
    this.loading.set(true);
    const state = this.mapEnvironmentToState(selectedEnvironment);
    const isActive = this.isActiveForEnvironment(selectedEnvironment);
    
    this.knowledgeBaseService.getArticlesByKnowledgeBaseId(
      kb.knowledgeBaseId, 
      state, 
      page, 
      this.pageSize(),
      this.activeFilters() || undefined,
      isActive
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          
          // Store in cache
          this.pageCache.set(cacheKey, {
            articles: response.articles,
            pagination: {
              totalItems: response.pagination.totalItems,
              totalPages: response.pagination.totalPages
            }
          });
          
          this.articles.set(response.articles);
          this.totalItems.set(response.pagination.totalItems);
          this.totalPages.set(response.pagination.totalPages);
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          this.snackBar.open(`Error al cargar artículos: ${error.message}`, 'Cerrar', { duration: 5000 });
        }
      });
  }

  /**
   * Get all picklists organized by type for modal selectors
   */
  private getAllPicklists(): Observable<{ types: any[], items: { [typeId: number]: any[] } }> {
    const kbId = this.knowledgeBase()?.knowledgeBaseId!;
    return this.picklistService.getPicklistTypes(kbId).pipe(
      switchMap(types => {
        if (types.length === 0) {
          return of({ types: [], items: {} });
        }
        
        // Get items for each type (including inactive ones for picklist editing)
        const itemRequests = types.map(type =>
          this.picklistService.getPicklistItems(kbId, type.picklistTypeId, false).pipe(
            map(items => ({ typeId: type.picklistTypeId, items }))
          )
        );
        
        return forkJoin(itemRequests).pipe(
          map(results => {
            const itemsByType: { [typeId: number]: any[] } = {};
            results.forEach(result => {
              itemsByType[result.typeId] = result.items;
            });
            return { types, items: itemsByType };
          })
        );
      })
    );
  }

  openEditModal(article: ArticleListItem): void {
    // Navigate using query params to avoid component recreation
    const queryParams: any = {
      article: article.knowledgeArticleId,
      env: article.state || 'DRAFT'
    };
    
    if (article.versionNumber) {
      queryParams.version = article.versionNumber;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
      state: { articleData: article } // Pass article data to avoid re-fetching
    });
  }

  /**
   * Maneja el click en un chip de publicación (QA o PROD)
   * Usa navegación por query params en lugar de abrir modales anidados
   */
  onPublicationChipClick(chipItem: any, article: ArticleListItem): void {
    if (!chipItem.hasVersion) {
      return; // No hacer nada si no hay versión
    }

    if (!article.knowledgeArticleId) {
      console.error('Missing articleId');
      return;
    }

    // Prevent multiple clicks while loading
    if (this.loadingChip()) {
      return;
    }


    // Set loading state for this specific chip
    const chipId = `${article.knowledgeArticleId}-${chipItem.environment}`;
    this.loadingChip.set(chipId);

    // Navigate using query params
    const queryParams: any = {
      article: article.knowledgeArticleId,
      env: chipItem.environment,
      version: chipItem.version
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge'
    });
  }

  /**
   * Open article modal from route - called when navigating with query params
   */
  private openArticleModalFromRoute(articleId: number, environment?: string, version?: number, articleData?: ArticleListItem): void {
    const picklists = this.picklistsData();
    
    if (this.isLoadingKnowledgeBase() || !picklists) {
      setTimeout(() => this.openArticleModalFromRoute(articleId, environment, version, articleData), 100);
      return;
    }

    const knowledgeBaseId = this.knowledgeBase()?.knowledgeBaseId;
    if (!knowledgeBaseId) {
      this.showError('No se pudo cargar la base de conocimiento');
      this.clearArticleQueryParams();
      return;
    }

    const env = (environment || 'DRAFT') as 'DRAFT' | 'QA' | 'PROD';
    const articleKey = `${articleId}-${env}-${version || 'current'}`;
    
    // If we have article data from router state or table, use it directly
    // Only fetch from API if:
    // 1. Navigating to a specific version (version !== undefined)
    // 2. No article data is available (e.g., direct URL navigation)
    const shouldFetchFromApi = version !== undefined || !articleData;
    
    if (!shouldFetchFromApi && articleData) {
      this.prepareArticleLoad(articleKey);
      const previousModalRef = this.currentArticleDialogRef;
      
      if (previousModalRef) {
        this.closeAllArticleModals(previousModalRef);
      }
      
      setTimeout(() => {
        this.openEditModalWithData(articleData, picklists, undefined, articleKey);
      }, 50);
      this.isLoadingArticle = false;
      return;
    }
    
    // Need to fetch from API
    this.prepareArticleLoad(articleKey);
    const previousModalRef = this.currentArticleDialogRef;
    const loadingSnackbar = this.showLoadingSnackbarIfNeeded(previousModalRef, version);
    
    this.knowledgeBaseService.getArticleByVersion(knowledgeBaseId, articleId, env, version)
      .subscribe({
        next: (response) => this.handleArticleLoadSuccess(
          response, articleKey, picklists, previousModalRef, loadingSnackbar
        ),
        error: (error) => this.handleArticleLoadError(
          error, previousModalRef, loadingSnackbar
        )
      });
  }

  /**
   * Prepare state for article loading
   */
  private prepareArticleLoad(articleKey: string): void {
    this.isLoadingArticle = true;
    this.currentArticleKey = articleKey;
  }

  /**
   * Show loading snackbar if no modal is open
   */
  private showLoadingSnackbarIfNeeded(
    previousModalRef: MatDialogRef<ArticleEditModalComponent> | null,
    version?: number
  ): MatSnackBarRef<any> | null {
    if (previousModalRef) {
      return null;
    }

    const versionText = version ? ` v${version}` : '';
    return this.snackBar.open(
      `Cargando artículo${versionText}...`,
      undefined,
      { duration: undefined }
    );
  }

  /**
   * Handle successful article load
   */
  private handleArticleLoadSuccess(
    response: any,
    articleKey: string,
    picklists: any,
    previousModalRef: MatDialogRef<ArticleEditModalComponent> | null,
    loadingSnackbar: MatSnackBarRef<any> | null
  ): void {
    this.isLoadingArticle = false;
    
    this.dismissSnackbar(loadingSnackbar);
    
    if (previousModalRef) {
      this.closeAllArticleModals(previousModalRef);
    }
    
    setTimeout(() => {
      this.openEditModalWithData(response.article, picklists, response.track, articleKey);
    }, 50);
  }

  /**
   * Handle article load error
   */
  private handleArticleLoadError(
    error: any,
    previousModalRef: MatDialogRef<ArticleEditModalComponent> | null,
    loadingSnackbar: MatSnackBarRef<any> | null
  ): void {
    this.isLoadingArticle = false;
    this.currentArticleKey = null;
    this.loadingChip.set(null);
    
    this.dismissSnackbar(loadingSnackbar);
    
    if (previousModalRef) {
      this.closeAllArticleModals(previousModalRef);
    }
    
    console.error('Error loading article:', error);
    this.showError('Error al cargar el artículo');
    this.clearArticleQueryParams();
  }

  /**
   * Dismiss snackbar if exists
   */
  private dismissSnackbar(snackbar: MatSnackBarRef<any> | null): void {
    if (snackbar) {
      snackbar.dismiss();
    }
  }

  /**
   * Close all article modals robustly
   */
  private closeAllArticleModals(previousModalRef: MatDialogRef<ArticleEditModalComponent>): void {
    this.closeModalByReference(previousModalRef);
    this.closeAllOpenArticleDialogs();
  }

  /**
   * Close modal by its reference
   */
  private closeModalByReference(modalRef: MatDialogRef<ArticleEditModalComponent>): void {
    const modalInstance = modalRef.componentInstance;
    
    modalInstance?.loadingSnackBarRef?.dismiss();
    modalRef.close();
  }

  /**
   * Close all open ArticleEditModalComponent dialogs
   */
  private closeAllOpenArticleDialogs(): void {
    this.dialog.openDialogs.forEach((dialogRef, index) => {
      
      if (dialogRef.componentInstance instanceof ArticleEditModalComponent) {
        const instance = dialogRef.componentInstance as ArticleEditModalComponent;
        instance.loadingSnackBarRef?.dismiss();
        dialogRef.close();
      }
    });
  }

  /**
   * Clear article-related query params
   */
  private clearArticleQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { article: null, env: null, version: null },
      queryParamsHandling: 'merge'
    });
  }

  /**
   * Open article edit modal with data
   */
  private openEditModalWithData(
    article: ArticleListItem, 
    picklists: { types: any[], items: { [typeId: number]: any[] } }, 
    track?: ArticleHistoryResponse, 
    articleKey?: string
  ): void {
    const isReadOnlyEnvironment = this.environmetFilter() === 'Producción' || this.environmetFilter() === 'QA' || this.environmetFilter() === 'Eliminados' || !!track;
    const knowledgeBaseId = this.knowledgeBase()?.knowledgeBaseId;
    
    const dialogRef = this.dialog.open(ArticleEditModalComponent, {
      width: '1300px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: { 
        knowledgeBaseId,
        article: { ...article },
        picklists,
        isReadOnly: isReadOnlyEnvironment,
        articleHistory: track
      }
    });

    // Limpiar el spinner del chip cuando el modal está completamente visible
    dialogRef.afterOpened().subscribe(() => this.loadingChip.set(null));

    this.currentArticleDialogRef = dialogRef;
    
    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => this.handleModalClosed(result, dialogRef, articleKey));
  }

  /**
   * Handle modal closed event
   */
  private handleModalClosed(
    result: any,
    dialogRef: MatDialogRef<ArticleEditModalComponent>,
    articleKey?: string
  ): void {
    const isCurrentModal = this.currentArticleDialogRef === dialogRef;
    
    this.clearModalReferenceIfCurrent(isCurrentModal);
    this.clearQueryParamsIfCurrent(isCurrentModal, articleKey);
    
    if (result) {
      this.clearCache();
      this.loadData();
      this.refreshKnowledgeBase(); // Refresh KB state to show synchronization indicator
    }
  }

  /**
   * Clear modal reference if this is the current modal
   */
  private clearModalReferenceIfCurrent(isCurrentModal: boolean): void {
    if (isCurrentModal) {
      this.currentArticleDialogRef = null;
    }
  }

  /**
   * Clear query params if this is the current modal
   */
  private clearQueryParamsIfCurrent(isCurrentModal: boolean, articleKey?: string): void {
    if (!isCurrentModal) {
      return;
    }

    if (articleKey && this.currentArticleKey === articleKey) {
      this.currentArticleKey = null;
      this.clearArticleQueryParams();
      return;
    }
    
    if (!articleKey) {
      this.clearArticleQueryParams();
      return;
    }
  }

  openCreateModal(): void {
    const picklists = this.picklistsData();
    const knowledgeBaseId = this.knowledgeBase()?.knowledgeBaseId;
    if (knowledgeBaseId) {
      this.handlePicklistsForCreateModal(knowledgeBaseId, picklists);
    } else {
      console.error('No knowledge base ID available for creating article');
      this.showError('No se puede crear el artículo: falta la base de conocimiento');
    }
  }

  /**
   * Handles loading picklists if needed and opening the create modal
   */
  private handlePicklistsForCreateModal(knowledgeBaseId: number, picklists: { types: any[], items: { [typeId: number]: any[] } } | null): void {
    if (!picklists) {
      // Fallback: load picklists if not available
      console.warn('Picklists not loaded, loading now...');
      this.getAllPicklists()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (loadedPicklists) => {
            this.picklistsData.set(loadedPicklists);
            this.openCreateModalWithData(knowledgeBaseId, loadedPicklists);
          },
          error: (error) => {
            console.error('Error loading picklists for modal:', error);
            this.showError('Error al cargar los datos necesarios');
          }
        });
    } else {
      // Use stored picklists - much faster!
      this.openCreateModalWithData(knowledgeBaseId, picklists);
    }
  }

  /**
   * Open create article modal with picklists data
   */
  private openCreateModalWithData(knowledgeBaseId: number, picklists: { types: any[], items: { [typeId: number]: any[] } }): void {
    const dialogRef = this.dialog.open(ArticleEditModalComponent, {
      width: '1300px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        article: null,
        knowledgeBaseId: knowledgeBaseId,
        kbName: this.knowledgeBase()?.name ?? '',
        picklists: picklists
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          // Clear cache and reload articles after successful update
          this.clearCache();
          this.loadData();
        }
      });
  }

  /**
   * Open picklist edit modal, loading picklists if not already loaded, and passing them for selector population
   */
  openPicklistModal(): void {
    const picklists = this.picklistsData();
    
    if (!picklists) {
      // Fallback: load picklists if not available
      console.warn('Picklists not loaded, loading now...');
      this.getAllPicklists()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (loadedPicklists) => {
            this.picklistsData.set(loadedPicklists);
            this.openPicklistModalWithData(loadedPicklists);
          },
          error: (error) => {
            console.error('Error loading picklists for modal:', error);
            this.showError('Error al cargar los datos necesarios');
          }
        });
    } else {
      // Use stored picklists - much faster!
      this.openPicklistModalWithData(picklists);
    }
  }

  /**
   * Open picklist edit modal with picklists data for selectors
   */
  private openPicklistModalWithData(picklists: { types: any[], items: { [typeId: number]: any[] } }): void {
    const kbId = this.knowledgeBase()?.knowledgeBaseId!;
    const dialogRef = this.dialog.open(PicklistEditModalComponent, {
      width: '700px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'custom-dialog-container',
      data: { 
        picklists: picklists, // Pasar los picklists para los selectores
        kbId: kbId
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result && result.success) {
          // Reload picklists cache first (for column formatters)
          this.loading.set(true);
          this.picklistService.clearCache();
          this.picklistService.loadAllPicklists(kbId).subscribe(() => {
            // Then reload picklists data for modals and reload articles
            this.loadAndStorePicklists();
            this.clearCache(); // Clear page cache after picklist changes
            this.loadData();
          });
        }
      });
  }

  /**
   * Open filters modal, loading picklists if not already loaded, and passing them for selector population, also passing active filters for initial selection in modal
   */
  openFiltersModal(): void {
    this.filtersOpen.set(true);
    const picklists = this.picklistsData();
    
    if (!picklists) {
      // Fallback: load picklists if not available
      console.warn('Picklists not loaded, loading now...');
      this.getAllPicklists()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (loadedPicklists) => {
            this.picklistsData.set(loadedPicklists);
            this.openFiltersModalWithData(loadedPicklists);
          },
          error: (error) => {
            this.filtersOpen.set(false);
            console.error('Error loading picklists for modal:', error);
            this.showError('Error al cargar los datos necesarios');
          }
        });
    } else {
      this.openFiltersModalWithData(picklists);
    }
  }

  /**
   * Open filters modal with picklists data for selectors and active filters for initial selection
   */
  private openFiltersModalWithData(picklists: { types: any[], items: { [typeId: number]: any[] } }): void {
    const dialogRef = this.dialog.open(FilterModalComponent, {
      width: '320px',
      maxWidth: '90vw',
      maxHeight: '100vh',
      panelClass: 'filter-modal-panel',
      backdropClass: 'filter-modal-backdrop',
      hasBackdrop: true,
      autoFocus: false,
      restoreFocus: false,
      data: {
        picklists: picklists,
        activeFilters: this.activeFilters() // Pasar filtros activos
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.filtersOpen.set(false);
        if (result?.clearFilters) {
          // Usuario limpió los filtros desde el modal
          this.activeFilters.set(null);
          this.clearCache(); // Clear cache when filters change
          this.currentPage.set(1);
          this.loadData();
        } else if (result) {
          // Usuario aplicó nuevos filtros
          this.activeFilters.set(result);
          this.clearCache(); // Clear cache when filters change
          this.currentPage.set(1); // Reset to first page when filters change
          this.loadData(); // Reload data with new filters
        }
      });
  }

  /**
   * Export selected articles, with error handling and user feedback 
   */
  exportCurrentViewArticles(): void {
    const currentArticles = this.articles();
    if (currentArticles.length === 0) {
      this.showError('No hay artículos en la vista actual para exportar');
      return;
    }
    this.exportSelectedArticles(currentArticles, 'la vista actual', 'Se exportarán los artículos cargados en la página actual.');
  }

  exportKnowledgeBaseArticles(): void {
    const kb = this.knowledgeBase();
    if (!kb) {
      this.showError('No se puede exportar la KB completa: falta la base de conocimiento');
      return;
    }
    const exportStatus = this.getExportStatusFromCurrentEnvironment();
    const exportEnvironmentLabel = this.getExportEnvironmentLabel(exportStatus);

    const dialogRef = this.dialog.open(ArticleExportModalComponent, {
      data: {
        scopeLabel: `toda la KB "${kb.name}"`,
        description: 'Se descargará un archivo Excel con todos los artículos de la base de conocimiento.',
        environmentLabel: exportEnvironmentLabel
      } as ArticleExportModalData,
      width: '600px',
      maxWidth: '90vw',
      disableClose: false,
      panelClass: 'article-export-modal'
    });

    dialogRef.componentInstance.confirmRequested
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
      dialogRef.componentInstance.setLoading(`Estamos preparando el archivo de ${exportEnvironmentLabel}. Esto puede tomar unos segundos.`);
      this.loading.set(true);
      this.knowledgeBaseService.exportArticlesToExcel(kb.knowledgeBaseId, exportStatus)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: ImportJobDownloadResponse) => {
            if (!response.downloadUrl) {
              this.loading.set(false);
              dialogRef.componentInstance.setError('No se recibió una URL de descarga para exportar.');
              return;
            }

            window.open(response.downloadUrl, '_blank', 'noopener,noreferrer');
            this.loading.set(false);
            dialogRef.componentInstance.setSuccess('Proceso terminado. La descarga ya fue solicitada.');
            this.showSuccess('La KB completa fue exportada exitosamente');
            setTimeout(() => dialogRef.close(true), 2500);
          },
          error: (error) => {
            console.error('Error exporting full knowledge base:', error);
            this.loading.set(false);
            dialogRef.componentInstance.setError('Error al exportar toda la KB. Por favor intente nuevamente.');
            this.showError('Error al exportar toda la KB. Por favor intente nuevamente.');
          }
        });
    });
  }

  /**
   * Delete selected articles, with error handling and user feedback
   */
  deleteArticles(): void {
    if (this.selectedRows.length === 0) {
      this.showError('No hay artículos seleccionados para eliminar');
      return;
    }
    this.deleteSelectedArticles(this.selectedRows);
  }

  private deleteSelectedArticles(articles: ArticleListItem[]): void {
    const ids = articles.map(row => row.knowledgeArticleId);
    
    if (ids.length === 0) {
      return;
    }
    
    const dialogRef = this.dialog.open(ArticleDeleteModalComponent, {
      data: { articles: articles },
      width: '600px',
      maxWidth: '90vw',
      disableClose: false,
      panelClass: 'article-delete-modal'
    });

    dialogRef.afterClosed().subscribe((result: ArticleDeleteModalResult | undefined) => {
      if (result) {
        this.loading.set(true);
        this.knowledgeBaseService.deleteArticles(ids, result.changeSummary || undefined)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              this.loading.set(false);
              const { summary, failed } = response;
              if (summary.failed === 0) {
                this.showSuccess(`${summary.succeeded} artículo(s) eliminado(s) exitosamente`);
              } else if (summary.succeeded > 0) {
                this.showSuccess(`${summary.succeeded} eliminado(s) correctamente, ${summary.failed} con errores`);
              } else {
                this.showError(`Error al eliminar los artículos: ${failed.map(f => f.error).join(', ')}`);
              }
              this.clearCache(); // Clear cache after deletion
              this.loadData();
              this.selectedRows = [];
            },
            error: (error) => {
              this.loading.set(false);
              console.error('Error eliminando artículos:', error);
              this.showError('Error al eliminar los artículos. Por favor intente nuevamente.');
            }
          });
      }
    });
  }

  /**
   * Publishes a single article using the unified publish logic
   */
  publishArticle(article: ArticleListItem): void {
    this.publishSelectedArticles([article]);
  }

  /**
   * Publishes selected articles to the next environment based on the current filter, with error handling and user feedback 
   */
  publishArticles(): void {
    if (this.selectedRows.length === 0) {
      this.showError('No hay artículos seleccionados para publicar.');
      return;
    }
    this.publishSelectedArticles(this.selectedRows);
  }

  /**
   * Unified method to publish articles (single or multiple)
   */
  private publishSelectedArticles(articles: ArticleListItem[]): void {
    const env = this.environmetFilter();
    const state = this.mapEnvironmentToState(env);
    const ids = this.getPublishIdsByEnvironment(env, articles);
    
    if (ids.length === 0) {
      return;
    }
    
    // Mostrar modal de confirmación
    const dialogRef = this.dialog.open(ArticlePublishConfirmModalComponent, {
      data: {
        articlesCount: ids.length,
        targetEnvironment: this.getNextEnvironmentLabel(env),
        currentEnvironment: env,
        articleIdentifiers: articles.map(a => a.identifier).filter(Boolean)
      } as ArticlePublishConfirmModalData,
      width: '600px',
      maxWidth: '90vw',
      disableClose: false,
      panelClass: 'article-publish-confirm-dialog'
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      // Proceder con la publicación
      this.loading.set(true);
      
      if (state) {
        this.knowledgeBaseService.publishArticles(ids, state)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: BatchCreateDraftsResponse) => {
            this.loading.set(false);
            this.processResponse(response, articles);
            this.clearCache(); // Clear cache after publishing
            this.loadData();
            this.refreshKnowledgeBase(); // Refresh KB state to show synchronization indicator
          },
          error: (error) => {
            this.loading.set(false);
            console.error('Error publicando artículo:', error);
            const errorMsg = articles.length === 1 
              ? 'Error al publicar el artículo. Por favor intente nuevamente.'
              : 'Error al publicar los artículos. Por favor intente nuevamente.';
            this.showError(errorMsg);
          }
        });  
      }
    });
  }

  /**
   * Devuelve los IDs a publicar según el ambiente. Muestra error si corresponde.
   */
  private getPublishIdsByEnvironment(env: string, rows: ArticleListItem[]): number[] {
    if (env === 'QA') {
      const withRevision = rows.filter(row => row.knowledgeArticleRevisionId !== undefined);
      const withoutRevision = rows.filter(row => row.knowledgeArticleRevisionId === undefined);
      const ids = withRevision.map(row => row.knowledgeArticleRevisionId!) as number[];
      if (withoutRevision.length > 0) {
        const idsNoRevision = withoutRevision.map(row => row.identifier || row.knowledgeArticleId).join(', ');
        this.showError(`Algunos artículos no tienen revisionId y no serán publicados: ${idsNoRevision}`);
      }
      return ids;
    } else {
      return rows.map(row => row.knowledgeArticleId);
    }
  }

  /**
   * Export articles to Excel file
   */
  exportSelectedArticles(articles: ArticleListItem[], scopeLabel?: string, description?: string): void {
    const ids = articles.map(row => row.knowledgeArticleId);
    
    if (ids.length === 0) {
      return;
    }
    
    // Mostrar modal de confirmación
    const dialogRef = this.dialog.open(ArticleExportModalComponent, {
      data: {
        articles: articles,
        scopeLabel: scopeLabel || (articles.length === 1 ? 'el artículo seleccionado' : `${articles.length} artículos`),
        description
      } as ArticleExportModalData,
      width: '600px',
      maxWidth: '90vw',
      disableClose: false,
      panelClass: 'article-export-modal'
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      // Proceder con la exportación
      this.loading.set(true);
      
      try {
        // Preparar datos para exportar
        const exportData = this.prepareExportData(articles);
        
        // Crear worksheet
        const worksheet = XLSX.utils.aoa_to_sheet(exportData);
        
        // Ajustar ancho de columnas
        worksheet['!cols'] = [
          { wch: 18 }, // Tipo
          { wch: 25 }, // Categoría 1
          { wch: 25 }, // Categoría 2
          { wch: 25 }, // Categoría 1 Salesforce
          { wch: 25 }, // Categoría 2 Salesforce
          { wch: 40 }, // Pregunta
          { wch: 50 }, // Contenido
          { wch: 30 }, // Palabras clave
          { wch: 20 }, // Acciones
          { wch: 20 }, // Id_solicitud
          { wch: 15 }, // Protocolo
          { wch: 25 }, // Protocolo_ejecutivo_voz
          { wch: 15 }, // IDENTIFICADOR
          { wch: 30 }, // Comentarios
          { wch: 30 }, // Comentario técnico
        ];
        
        // Crear workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Articulos');
        
        // Generar nombre de archivo con timestamp
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `articulos_${timestamp}.xlsx`;
        
        // Descargar archivo
        XLSX.writeFile(workbook, filename);
        
        this.loading.set(false);
        this.showSuccess(
          articles.length === 1 
            ? 'Artículo exportado exitosamente'
            : `${articles.length} artículos exportados exitosamente`
        );
      } catch (error) {
        console.error('Error exporting articles:', error);
        this.loading.set(false);
        this.showError('Error al exportar los artículos. Por favor intente nuevamente.');
      }
    });
  }

  /**
   * Prepare article data for Excel export
   */
  private prepareExportData(articles: ArticleListItem[]): any[][] {
    // Headers (mismo orden que el template de importación)
    const headers = [
      'Tipo',
      'Categoría 1',
      'Categoría 2',
      'Categoría 1 Salesforce',
      'Categoría 2 Salesforce',
      'Pregunta',
      'Contenido',
      'Palabras clave',
      'Acciones',
      'Id_solicitud',
      'Protocolo',
      'Protocolo_ejecutivo_voz',
      'IDENTIFICADOR',
      'Comentarios',
      'Comentario técnico',
    ];
    
    const picklistName = (id: number | null | undefined): string => {
      if (!id) return '';
      const name = this.picklistService.getItemName(id);
      return name === '-' ? '' : name;
    };

    // Mapear cada artículo a una fila
    const rows = articles.map(article => [
      picklistName(article.requestTypePicklistItemId),
      picklistName(article.category1PicklistItemId),
      picklistName(article.category2PicklistItemId),
      picklistName(article.category1SalesforcePicklistItemId),
      picklistName(article.category2SalesforcePicklistItemId),
      article.question || '',
      article.content || '',
      article.keywords || '',
      picklistName(article.actionsPicklistItemId),
      picklistName(article.businessPicklistItemId),
      article.protocol || '',
      article.executiveVoiceProtocol || '',
      article.identifier || '',
      article.comments || '',
      article.technicalComment || '',
    ]);
    
    // Retornar headers + rows
    return [headers, ...rows];
  }

  /**
   * Procesa la respuesta de publicación, mostrando mensajes de éxito y error según corresponda, y un resumen si hay mezcla de resultados
   */
  private processResponse(response: BatchCreateDraftsResponse, originalArticles: ArticleListItem[]): void {
    const isPlural = originalArticles.length > 1;
    
    // Mostrar errores si existen
    if (response.failed.length > 0) {
      this.showError(this.getErrorMessageForPublish(response, isPlural));
      // Si hay errores, no mostramos mensajes de éxito para los fallidos
      // pero sí mostramos los éxitos si existen
    }
    if (response.successful.length > 0) {
      this.showSuccess(this.getSuccessMessageForPublish(response, isPlural, originalArticles));
    }
    
    // Si hay una mezcla de éxitos y fallos, mostrar resumen
    if (response.failed.length > 0 && response.successful.length > 0) {
      const summary = `Resumen: ${response.successful.length} exitosos, ${response.failed.length} fallidos de ${response.summary.total} total.`;
      this.showError(summary);
    }
  }

  /**
   * Genera un mensaje de error detallado para la publicación, listando los errores específicos de cada artículo fallido
   */
  private getErrorMessageForPublish(response: BatchCreateDraftsResponse, isPlural: boolean): string {
    const failedCount = response.failed.length;
    const errorMessages = response.failed.map(failure => `- ${failure.error}`).join('\n');
    
    if (isPlural) {
      return `${failedCount} artículos no pudieron ser publicados:\n${errorMessages}`;
    }
    return `No se pudo publicar el artículo:\n${errorMessages}`;
  }

  /**
   * Genera un mensaje de éxito para la publicación, indicando cuántos artículos fueron publicados correctamente y a qué ambiente, sin mencionar los fallidos
   */
  private getSuccessMessageForPublish(response: BatchCreateDraftsResponse, isPlural: boolean, originalArticles: ArticleListItem[]): string {
    const nextEnv = this.getNextEnvironmentLabel(this.environmetFilter());
    const successCount = response.successful.length;
    
    if (isPlural) {
      return `${successCount} artículos fueron publicados correctamente a ${nextEnv}.`;
    }
    return `El artículo fue publicado correctamente a ${nextEnv}.`;
  }

  /**
   * Devuelve la etiqueta del siguiente ambiente basado en el ambiente actual
   */
  getNextEnvironmentLabel(current: string): string {
    switch (current) {
      case 'Borrador':
        return 'QA';
      case 'DRAFT':
        return 'QA';
      case 'QA':
        return 'Producción';
      default:
        return current;
    }
  }

  /**
   * Maneja el cambio de selección en la tabla, actualizando el estado de las filas seleccionadas
   */
  onSelectionChange(selectedRows: ArticleListItem[]): void {
    this.selectedRows = selectedRows;
  }

  /**
   * Abre el modal de importación de artículos, pasando el ID de la base de conocimiento, y recarga los datos después de una importación exitosa
   */
  importArticles(): void {
    const code = this.knowledgeBase()?.code;
    if (!code) {
      this.showError('No se puede importar artículos: falta la base de conocimiento');
      return;
    }
    this.router.navigate(['/knowledge-base', code, 'imports']);
  }

  /**
   * Map environment filter to API state values
   */
  private mapEnvironmentToState(environment: string): string | undefined {
    const mapping: Record<string, string> = {
      'Producción': 'PROD',
      'QA': 'QA',
      'Borrador': 'DRAFT',
      'Eliminados': 'DRAFT'
    };
    return mapping[environment];
  }

  private getExportStatusFromCurrentEnvironment(): 'DRAFT' | 'QA' | 'PROD' {
    const state = this.mapEnvironmentToState(this.environmetFilter());
    if (state === 'QA' || state === 'PROD') {
      return state;
    }
    return 'DRAFT';
  }

  private getExportEnvironmentLabel(status: 'DRAFT' | 'QA' | 'PROD'): string {
    switch (status) {
      case 'QA':
        return 'QA';
      case 'PROD':
        return 'Producción';
      default:
        return 'Borrador';
    }
  }

  private isActiveForEnvironment(environment: string): boolean {
    return environment !== 'Eliminados';
  }

  /**
   * Generate cache key based on current filter state
   */
  private getCacheKey(page: number): string {
    const env = this.environmetFilter();
    const filters = JSON.stringify(this.activeFilters() || {});
    const pageSize = this.pageSize();
    return `${env}-${filters}-${pageSize}-${page}`;
  }

  /**
   * Clear the entire page cache
   */
  private clearCache(): void {
    this.pageCache.clear();
  }

  /**
   * Static configuration for knowledge base status icons and colors
   */
  private readonly KNOWLEDGE_BASE_STATUS_MAP: Record<string, {icon: any; iconClass: string; color: string}> = {
    'SYNCHRONIZING': { 
      icon: RefreshCw, 
      iconClass: 'animate-spin',
      color: 'bg-gray-400 dark:bg-gray-500' 
    },
    'ERROR': { 
      icon: null, 
      iconClass: '',
      color: 'bg-amber-500 dark:bg-amber-400' 
    },
    'null': { 
      icon: null, 
      iconClass: '',
      color: 'bg-green-500 dark:bg-green-400' 
    }
  };

  /**
   * Get icon and color configuration based on knowledge base synchronization status
   */
  getKnowledgeBaseConfig(synchronizationStatus: string | null): { icon: any; iconClass: string; color: string } {
    if (synchronizationStatus === 'SYNCHRONIZING') {
      return this.KNOWLEDGE_BASE_STATUS_MAP['SYNCHRONIZING'];
    } else if (synchronizationStatus === 'ERROR') {
      return this.KNOWLEDGE_BASE_STATUS_MAP['ERROR'];
    } else { //null
      return this.KNOWLEDGE_BASE_STATUS_MAP['null'];
    }
  }
  
  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['success-snackbar']
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 5000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }

  goBack(): void {
    this.router.navigate(['/knowledge-base']);
  }

  goToSynchronizations(): void {
    const kb = this.knowledgeBase();
    if (!kb) return;
    
    this.router.navigate(['/knowledge-base', kb.code, 'synchronizations']);
  }

  ngOnDestroy(): void {
    this.clearCache(); // Clean up cache on component destroy
    this.destroy$.next();
    this.destroy$.complete();
  }
}
